import type { Attribution, WireEvent } from "@watchover/shared";
import { getCookie, setCookie } from "./cookies.js";
import { storageKey } from "./ids.js";
import { scrubUrl } from "./url.js";

/**
 * Builds the one-per-session enrichment event: who acquired this visitor (UTM,
 * click ids, referrer, first- vs last-touch) and everything about their client
 * we can read without prompting (screen, timezone, language, platform, network).
 *
 * First-touch attribution is persisted in a root-domain cookie so it survives
 * across sessions and subdomains; last-touch reflects the current landing.
 */

const CLICK_ID_KEYS = [
  "gclid",
  "fbclid",
  "msclkid",
  "ttclid",
  "twclid",
  "dclid",
  "gbraid",
  "wbraid",
  "li_fat_id",
  "yclid",
  "igshid",
];
const ATTR_COOKIE = "wo_attr";

type ContextEvent = Extract<WireEvent, { t: "context" }>;

export function buildContextEvent(cookieDomain?: string): ContextEvent {
  const params = safeParams();
  const rawReferrer = safe(() => document.referrer) || undefined;
  // Keep the domain for attribution, but scrub tokens out of the full referrer.
  const referrer = scrubUrl(rawReferrer) || undefined;
  const referringDomain = rawReferrer ? hostOf(rawReferrer) : undefined;
  const landingPath = safe(() => location.pathname) || undefined;

  const lastTouch: Attribution = {
    source: param(params, "utm_source"),
    medium: param(params, "utm_medium"),
    campaign: param(params, "utm_campaign"),
    term: param(params, "utm_term"),
    content: param(params, "utm_content"),
    referrer,
    landingPath,
  };

  const clickIds = collectClickIds(params);
  const firstTouch = resolveFirstTouch(lastTouch, cookieDomain);

  const ev: ContextEvent = {
    t: "context",
    ts: Date.now(),
    utmSource: lastTouch.source,
    utmMedium: lastTouch.medium,
    utmCampaign: lastTouch.campaign,
    utmTerm: lastTouch.term,
    utmContent: lastTouch.content,
    clickIds: Object.keys(clickIds).length ? clickIds : undefined,
    referrer,
    referringDomain,
    landingPath,
    firstTouch,
    ...clientFacets(),
  };
  return prune(ev);
}

// ── attribution ───────────────────────────────────────────────────────────────

function collectClickIds(params: URLSearchParams | null): Record<string, string> {
  const out: Record<string, string> = {};
  if (!params) return out;
  for (const k of CLICK_ID_KEYS) {
    const v = params.get(k);
    if (v) out[k] = v.slice(0, 255);
  }
  return out;
}

/** Read first-touch from cookie, or set it now (persisted ~2 years). */
function resolveFirstTouch(
  current: Attribution,
  cookieDomain?: string,
): Attribution | undefined {
  const existing = safe(() => getCookie(storageKey(ATTR_COOKIE)));
  if (existing) {
    try {
      return JSON.parse(existing) as Attribution;
    } catch {
      /* fall through and rewrite */
    }
  }
  const first: Attribution = { ...current, ts: Date.now() };
  // Drop empty keys so the cookie stays small.
  const compact = prune(first);
  try {
    setCookie(storageKey(ATTR_COOKIE), JSON.stringify(compact), {
      domain: cookieDomain,
      days: 730,
    });
  } catch {
    /* ignore */
  }
  return compact;
}

// ── client facets ─────────────────────────────────────────────────────────────

function clientFacets(): Partial<ContextEvent> {
  const nav = navigator as any;
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
  return {
    screenW: safe(() => screen.width),
    screenH: safe(() => screen.height),
    dpr: safe(() => Math.round((window.devicePixelRatio || 1) * 100) / 100),
    language: safe(() => navigator.language),
    languages: safe(() =>
      Array.isArray(navigator.languages)
        ? navigator.languages.slice(0, 5)
        : undefined,
    ),
    timezone: safe(
      () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    ),
    tzOffset: safe(() => -new Date().getTimezoneOffset()),
    platform: safe(() => nav.userAgentData?.platform || navigator.platform),
    deviceMemory: safe(() => nav.deviceMemory),
    cores: safe(() => navigator.hardwareConcurrency),
    touch: safe(
      () => "ontouchstart" in window || navigator.maxTouchPoints > 0,
    ),
    colorScheme: safe(() =>
      window.matchMedia?.("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light",
    ),
    connection: conn
      ? prune({
          effectiveType: conn.effectiveType,
          downlink: conn.downlink,
          rtt: conn.rtt,
          saveData: conn.saveData,
        })
      : undefined,
  };
}

// ── helpers ───────────────────────────────────────────────────────────────────

function safeParams(): URLSearchParams | null {
  return safe(() => new URLSearchParams(location.search)) ?? null;
}
function param(p: URLSearchParams | null, key: string): string | undefined {
  const v = p?.get(key);
  return v ? v.slice(0, 255) : undefined;
}
function hostOf(url: string): string | undefined {
  return safe(() => new URL(url).hostname) || undefined;
}
function safe<T>(fn: () => T): T | undefined {
  try {
    const v = fn();
    return v === null ? undefined : v;
  } catch {
    return undefined;
  }
}
/** Remove undefined/empty values so payloads stay compact. */
function prune<T extends Record<string, any>>(obj: T): T {
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v === undefined || v === "" || (typeof v === "object" && v && Object.keys(v).length === 0)) {
      delete obj[k];
    }
  }
  return obj;
}
